import {Button} from 'antd'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import AsyncAction from 'modules/asyncCache/components/AsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {BsInboxesFill} from 'react-icons/bs'
import {useLocation} from 'react-router-dom'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {useRouteSearchParam} from '../../../envs/_current/history'
import WorkspaceContext from '../WorkspaceContext'
import {WorkspaceOrgDeptContainerContext} from './WorkspaceOrgDeptContainer'
import {
    group_approveLink_Api,
    group_denyLink_Api,
    group_getPendingLinks_Api,
    organization_getGroups_Api,
} from '../../../apis'
import {useOrganizationGroupForm} from '../../AppLocals/Organization/functions/useOrganizationGroupForm'
import {OrganizationActionTypes} from '../Host/WorkspaceMembers'
import {FaUsers} from 'react-icons/fa6'
import {PiUserCircleCheckFill} from 'react-icons/pi'
import {SaintsOutlineSvg} from '../../../components/Svgs/SaintsPlaceholder'
import {formatNumber} from '../../../helpers/number'
import classNames from 'classnames'
import {GoDotFill} from "react-icons/go";
import {LoadingOutlined} from '@ant-design/icons'

export const DepartmentCodes = [
    {
        value: 'LEADERSHIP',
        label: 'leadership',
    },
]

const GROUP_ICON_COLORS = [
    {bg: 'bg-indigo-50', icon: 'text-indigo-500', badge: 'bg-indigo-50 text-indigo-600 border-indigo-100'},
    {bg: 'bg-teal-50', icon: 'text-teal-500', badge: 'bg-teal-50 text-teal-600 border-teal-100'},
    {bg: 'bg-yellow-50', icon: 'text-yellow-500', badge: 'bg-yellow-50 text-yellow-500 border-yellow-100'},
    {bg: 'bg-pink-50', icon: 'text-pink-600', badge: 'bg-pink-50 text-pink-600 border-pink-100'},
    {bg: 'bg-blue-100', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-600 border-blue-100'},
]

const hashGroupTitle = (str) => {
    let h = 0
    for (let i = 0; i < (str || '').length; i++) {
        h = (Math.imul(31, h) + str.charCodeAt(i)) | 0
    }
    return Math.abs(h)
}

const getGroupColor = (group_title) =>
    GROUP_ICON_COLORS[hashGroupTitle(group_title) % GROUP_ICON_COLORS.length] ?? GROUP_ICON_COLORS[0]

const DepartmentListItem = ({item, t, onSelect, renderContextMenu}) => {
    const colors = getGroupColor(item.group_title)
    const groupLabel = item.group_title || null

    const handleSelect = () => onSelect({current_department_id: item.id})

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSelect()
                }
            }}
            className="p-4 flex items-start gap-3 transition-colors duration-300 cursor-pointer custom-item-render relative list-item">
            <div
                style={{ width: 40, height: 40 }}
                className={classNames('mt-1 p-2 flex flex-center flex-shrink-0 rounded-xl', colors.bg)}>
                <FaUsers size={20} className={colors.icon}/>
            </div>
            <div className="flex flex-col flex-1 min-w-0 gap-1">
                <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-bold text-base text-color-000 leading-snug truncate hover:text-primary transition-colors">
                        {item.title}
                    </h3>
                    {!!item?.active_as_member?.id && (
                        <PiUserCircleCheckFill
                            size={14}
                            className="text-green-500 flex-shrink-0"
                            title={t('joined')}
                        />
                    )}
                    {groupLabel && (
                        <span
                            className={classNames(
                                'hidden sm:inline-flex px-1.5 py-0.5 text-xs font-medium rounded border truncate max-w-[160px]',
                                colors.badge
                            )}>
                            {groupLabel}
                        </span>
                    )}
                </div>
                {groupLabel && (
                    <span
                        style={{ padding: '3px 10px' }}
                        className={classNames('w-fit font-medium tracking-wide text-xs italic rounded-full leading-tight', colors.badge)}>
                        {groupLabel}
                    </span>
                )}
                <div className="mt-2 mb-1 border-b border-color-50"/>

                {(item?.member_count > 0 || item?.patron_saint_name) && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {item?.member_count > 0 && (
                            <span className="flex items-center gap-1 shrink-0 text-color-400 tracking-wider">
                            <GoDotFill size={10} className="text-color-500"/>
                            <span className="font-semibold text-color-100">
                                {formatNumber(item?.member_count ?? 0)}
                            </span>
                                {t('members').toLowerCase()}
                        </span>
                        )}
                        {item?.patron_saint_name && (
                            <span className="flex items-center gap-1 text-color-100 tracking-wider">
                            <SaintsOutlineSvg size={11} className="text-blue-500 flex-shrink-0"/>
                            <span className="font-semibold truncate">
                                {item.patron_saint_name}
                                {item.patron_saint_feast_day && (
                                    <span className="font-medium text-xs text-gray-500 italic"> ({item.patron_saint_feast_day})</span>
                                )}
                            </span>
                        </span>
                        )}
                    </div>
                )}

                {item?.description && (
                    <div className="mt-1 text-color-300 tracking-wider">
                        {item?.description}
                    </div>
                )}
            </div>
            {_.isFunction(renderContextMenu) && (
                <div
                    className="absolute right-8 top-8 contextIcon"
                    onClick={(e) => e.stopPropagation()}>
                    {renderContextMenu(item)}
                </div>
            )}
        </div>
    )
}

const renderDepartment = ({onSelectWorkspaceOrgDeptContainerRoute, t, renderContextMenu = NullAsDefault} = {}) =>
    (item) => (
        <DepartmentListItem
            key={item.id}
            item={item}
            t={t}
            onSelect={onSelectWorkspaceOrgDeptContainerRoute}
            renderContextMenu={renderContextMenu}
        />
    )

const PendingGroupLinksSection = ({ownerId, t}) => {
    const {response: rawData, isLoading} = useAsync({
        apiInfo: group_getPendingLinks_Api,
        query: {':owner_type': 'organization', ':owner_id': ownerId},
        deps: [ownerId],
    })

    const links = useMemo(() => {
        const arr = _.get(rawData, 'data.data', _.get(rawData, 'data', []))
        return Array.isArray(arr) ? arr : []
    }, [rawData])

    if (!ownerId || (!isLoading && links.length === 0)) return null

    return (
        <div className="background rounded-xl border border-color-50 shadow-sm overflow-hidden">
            <div className="px-3 py-2.5 border-b border-color-50 flex items-center gap-1.5">
                <FaUsers size={13} className="text-color-400 flex-shrink-0"/>
                <span className="text-xs font-bold uppercase tracking-wider text-color-300">
                    {t('pending group link requests')}
                </span>
            </div>
            {isLoading && !links.length ? (
                <div className="flex items-center justify-center py-6">
                    <LoadingOutlined className="text-primary"/>
                </div>
            ) : (
                links.map((link) => (
                    <div
                        key={link.id}
                        className="px-3 py-3 flex items-center gap-3 border-b border-color-50 last:border-b-0">
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-color-000 truncate mb-0.5">
                                {_.get(link, 'group.title', '')}
                            </p>
                            {_.get(link, 'group.group_title') && (
                                <p className="text-xs text-color-400 truncate">
                                    {link.group.group_title}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <AsyncAction apiInfo={group_denyLink_Api} query={{':id': link.id}}>
                                {({success, handleAsyncAction, isLoading: denying}) => (
                                    success ? (
                                        <span className="text-xs font-semibold text-red-400 italic">
                                            {t('rejected')}
                                        </span>
                                    ) : (
                                        <Button size="small" loading={denying} onClick={() => handleAsyncAction()}>
                                            {t('reject')}
                                        </Button>
                                    )
                                )}
                            </AsyncAction>
                            <AsyncAction apiInfo={group_approveLink_Api} query={{':id': link.id}}>
                                {({success, handleAsyncAction, isLoading: approving}) => (
                                    success ? (
                                        <span className="text-xs font-semibold text-green-600 italic">
                                            {t('accepted')}
                                        </span>
                                    ) : (
                                        <Button
                                            type="primary" size="small"
                                            loading={approving}
                                            onClick={() => handleAsyncAction()}>
                                            {t('approve')}
                                        </Button>
                                    )
                                )}
                            </AsyncAction>
                        </div>
                    </div>
                ))
            )}
        </div>
    )
}

const WorkspaceOrganizationGroupList = ({
    filter_actions = {
        sortBy: 'sort-by',
        keyword: 'keyword',
    },
}) => {
    const t = useTranslate()
    const location = useLocation()
    const {organization} = useContext(WorkspaceContext)
    const owner_id = organization?.id
    const owner_type = 'organization'
    const [refreshKey, setRefreshKey] = useState()
    const {onSelectWorkspaceOrgDeptContainerRoute} = useContext(WorkspaceOrgDeptContainerContext)
    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const {modal, handleAction, refreshToken} = useOrganizationGroupForm({
        organization,
    })

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: OrganizationActionTypes.DEPT_UPDATE,
                    },
                    {
                        type: 'divider',
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: OrganizationActionTypes.DEPT_DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        [handleAction]
    )

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            refreshToken,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, refreshToken, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({})
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            renderItem: renderDepartment({
                t,
                onSelectWorkspaceOrgDeptContainerRoute,
                renderContextMenu,
            }),
            PaginationMeta: NullAsDefault,
            apiInfo: organization_getGroups_Api,
            query: bindQueryParams([{id: owner_id}]),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
            renderEmpty: () => (
                <div className="p-8 flex flex-col items-center gap-3 border border-color-50 rounded-xl background">
                    <BsInboxesFill size={40} className="text-color-200"/>
                    <span className="text-center text-sm text-color-300 tracking-wide">
                        {t('no data is available now')}
                    </span>
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: true,
        query_params: filter_actions,
        AdvancedFilter: NullAsDefault,
        renderFilter: NullAsDefault,
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t('list of group / ministry')}
                        </h2>
                        <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                        <div className="mt-1 mr-4 text-sm text-color-300 tracking-wider">
                            {t('manage groups and ministries in your organization')}
                        </div>
                    </div>
                    <Button
                        color="primary" variant="solid"
                        className="px-6 py-2 font-semibold rounded-lg"
                        onClick={() => {
                            handleAction(OrganizationActionTypes.DEPT_CREATE)
                        }}>
                        {t('create')}
                    </Button>
                </div>
                {searchBar}
                {filterBar}
            </div>
        ),
    })

    return (
        <div className="flex flex-1 flex-col gap-4">
            <PendingGroupLinksSection ownerId={owner_id} t={t}/>
            {pager_widget}
            {modal}
        </div>
    )
}

export default WorkspaceOrganizationGroupList
